#!/usr/bin/env node
// nav-tables.mjs — our own sight reduction tables and almanac, COMPUTED.
//
// FOUNDER, 2026-08-02: "can we use bowditch fully and build our own tables?"
//
// Yes to both, and the second matters more. The corpus cites Bowditch 521 times and the sight reduction
// tables ZERO times, because the explanation of celestial navigation is freely available while the
// PROCEDURE a candidate actually sits the exam on is not: HO 229/249, Norie's and the Nautical Almanac
// are copyrighted published works.
//
// But a table of computed values is not knowledge anyone owns — it is arithmetic. HO 229 is the cosine
// formula evaluated at integer arguments and printed. We already compute that formula exactly, and
// verify it against an independently-derived 3D sphere. So we generate our own, from first principles,
// in our own arrangement. Nothing is copied: no existing table is read, transcribed or reformatted.
//
// WHAT THIS IS NOT. This is a TEACHING instrument. The ephemeris below is the standard low-precision
// solar theory — good to about 0.01 degrees, which is roughly 0.6 of a minute of arc. That is ample for
// a candidate learning the method and for generating practice sights with known answers. It is NOT a
// substitute for the Nautical Almanac at sea, and the code says so at every exit rather than letting a
// number look more authoritative than it is.
//
//   node nav-tables.mjs verify
//   node nav-tables.mjs sr --lat 35 --dec 12          # a sight reduction page, computed
//   node nav-tables.mjs sun --date 2026-06-21T12:00Z  # the sun's GHA and declination
import { calculatedAltitude, azimuth } from './pzx.mjs';
import { moon as moonECL } from './elp-moon.mjs';
import { apparentPlace, catalogue, catalogueSource } from './stars.mjs';
import { heliocentric, planetNames } from './vsop87-planets.mjs';

const D = Math.PI / 180;
const sin = (d) => Math.sin(d * D), cos = (d) => Math.cos(d * D);
const norm360 = (a) => ((a % 360) + 360) % 360;
const r = (x, n = 4) => +x.toFixed(n);

/** Degrees to the sexagesimal a navigator writes: 23° 26.3' */
export function dm(deg, { signed = false } = {}) {
  const s = deg < 0 ? '-' : (signed ? '+' : '');
  const a = Math.abs(deg);
  let d = Math.floor(a), m = (a - d) * 60;
  if (+m.toFixed(1) >= 60) { d += 1; m = 0; }
  return `${s}${d}° ${m.toFixed(1)}'`;
}

// ── the ephemeris ───────────────────────────────────────────────────────────────────────────────
// Low-precision solar position (the standard formulation published in the Astronomical Almanac's
// explanatory material and reproduced in every textbook — mathematics, not anyone's text). Accuracy
// about 0.01 degrees in the period 1950-2050. Good enough to teach with and stated plainly.
export function sun(date) {
  const t = date instanceof Date ? date : new Date(date);
  const jd = t.getTime() / 86400000 + 2440587.5;
  const n = jd - 2451545.0;                              // days from J2000.0
  const L = norm360(280.460 + 0.9856474 * n);            // mean longitude
  const g = norm360(357.528 + 0.9856003 * n);            // mean anomaly
  const lambda = norm360(L + 1.915 * sin(g) + 0.020 * sin(2 * g));   // ecliptic longitude
  const eps = 23.439 - 0.0000004 * n;                    // obliquity
  // right ascension and declination from the ecliptic
  const ra = norm360(Math.atan2(cos(eps) * sin(lambda), cos(lambda)) / D);
  const dec = Math.asin(sin(eps) * sin(lambda)) / D;
  // Greenwich hour angle: GHA = GMST - RA, with GMST from the same epoch
  const gmst = norm360(280.46061837 + 360.98564736629 * n);
  const gha = norm360(gmst - ra);
  // equation of time, in minutes of time — the difference between apparent and mean solar time
  const eot = ((norm360(L - ra) + 180) % 360 - 180) * 4;
  return {
    kind: 'computed', utc: t.toISOString(),
    gha: r(gha), dec: r(dec), sha: r(norm360(360 - ra)), ra: r(ra),
    gha_dm: dm(gha), dec_dm: dm(dec, { signed: true }),
    equation_of_time_min: r(eot, 2),
    accuracy: 'about 0.01° (0.6\') — low-precision solar theory, adequate for teaching and practice sights',
    not_for: 'NOT a substitute for the Nautical Almanac in a real fix',
  };
}

// ── ALMANAC-GRADE EPHEMERIS ──────────────────────────────────────────────────────────────────────
// FOUNDER: "i am sure we can match nautical almanac too, u may have to make harder arrangements."
//
// He is right. The Nautical Almanac tabulates GHA and declination to 0.1', and the three-term series
// above cannot hold that — it is good to about 0.6'. The higher-order solar theory can: the equation of
// centre carried to three harmonics with time-dependent coefficients, the apparent longitude corrected
// for nutation and aberration, the obliquity with its secular terms, and sidereal time with its T^2 and
// T^3 terms. That is ~0.01' — an order of magnitude INSIDE the tabulation, so the rounding to 0.1' is
// ours and not the theory's.
//
// Still nobody's copyright: this is celestial mechanics, published in every textbook since Newcomb, and
// not one figure is transcribed from an almanac. What we cannot claim is the ALMANAC'S authority — see
// the honesty block at the end of this section, which is the part that must never be quietly dropped.
const T_of = (jd) => (jd - 2451545.0) / 36525;

// ΔT — TERRESTRIAL TIME minus UTC. An ephemeris series is a function of TT (dynamical time); sidereal
// time is a function of UT1. Feeding UTC to both leaves a systematic error proportional to how fast the
// body moves: measured, the Moon's GHA was a constant +0.702' out (42", and the Moon moves 0.549"/s —
// 77 seconds of time) while its declination was centred on zero. The Sun showed the same fault at
// 1/13th the size, which is exactly the ratio of their motions, and that is what identified it.
//
// TT = TAI + 32.184 s, and TAI − UTC = 37 s since the 2017 leap second. Leap seconds are DECIDED, not
// computed, so this is a table that must be updated when the IERS announces one — not a formula.
const TAI_MINUS_UTC = 37.0;                       // valid from 2017-01-01; update on the next leap second
const DELTA_T_SEC = TAI_MINUS_UTC + 32.184;       // 69.184 s
const jde_of = (t) => jd_of(t) + DELTA_T_SEC / 86400;   // Julian Ephemeris Day (TT)
const jd_of = (t) => (t instanceof Date ? t : new Date(t)).getTime() / 86400000 + 2440587.5;
const poly = (T, ...c) => c.reduce((a, k, i) => a + k * T ** i, 0);

/** Nutation in longitude and obliquity — the four dominant terms (IAU 1980, abridged). Arcseconds. */
function nutation(T) {
  const om = norm360(poly(T, 125.04452, -1934.136261, 0.0020708, 1 / 450000));   // ascending node of the Moon
  const Ls = norm360(poly(T, 280.4665, 36000.7698));                            // mean longitude of the Sun
  const Lm = norm360(poly(T, 218.3165, 481267.8813));                           // mean longitude of the Moon
  const dpsi = -17.20 * sin(om) - 1.32 * sin(2 * Ls) - 0.23 * sin(2 * Lm) + 0.21 * sin(2 * om);
  const deps = 9.20 * cos(om) + 0.57 * cos(2 * Ls) + 0.10 * cos(2 * Lm) - 0.09 * cos(2 * om);
  return { dpsi, deps, om };
}

/** The Sun to almanac precision. Returns the same shape as sun() so callers can swap without change. */
export function sunPrecise(date) {
  const t = date instanceof Date ? date : new Date(date);
  const jd = jd_of(t), T = T_of(jd);
  // geometric mean longitude and mean anomaly, with their secular terms
  const L0 = norm360(poly(T, 280.46646, 36000.76983, 0.0003032));
  const M = norm360(poly(T, 357.52911, 35999.05029, -0.0001537));
  // equation of centre — three harmonics, coefficients themselves time-dependent
  const C = poly(T, 1.914602, -0.004817, -0.000014) * sin(M)
          + poly(T, 0.019993, -0.000101) * sin(2 * M)
          + 0.000289 * sin(3 * M);
  // PLANETARY PERTURBATIONS. Without these the theory sat ~0.13' out — measured against the published
  // 2026 March equinox, where the apparent longitude was 19.6" ahead, which is very close to one
  // aberration constant and was the clue. These are the classic perturbation terms: Venus (A, B),
  // Jupiter (C, H), the Moon (D) and the long-period term (E). Each is a real physical pull on the
  // earth's orbit, not a fudge factor.
  const A = norm360(153.23 + 22518.7541 * T);      // Venus
  const B = norm360(216.57 + 45037.5082 * T);      // Venus
  const Cp = norm360(312.69 + 32964.3577 * T);     // Jupiter
  const Dp = norm360(350.74 + 445267.1142 * T - 0.00144 * T * T);   // the Moon
  const E = norm360(231.19 + 20.20 * T);           // long period
  const H = norm360(353.40 + 65928.7155 * T);      // Jupiter
  const pert = 0.00134 * cos(A) + 0.00154 * cos(B) + 0.00200 * cos(Cp)
             + 0.00179 * sin(Dp) + 0.00178 * sin(E) + 0.00001 * cos(H);
  const trueLon = L0 + C + pert;
  const nut = nutation(T);
  // apparent longitude: nutation in longitude plus aberration (-20.4898" / R, here to sufficient order)
  const lambda = norm360(trueLon + nut.dpsi / 3600 - 0.005691611);
  // mean obliquity with its secular terms, then the nutation in obliquity
  const eps0 = 23 + 26 / 60 + 21.448 / 3600 + poly(T, 0, -46.8150, -0.00059, 0.001813) / 3600;
  const eps = eps0 + nut.deps / 3600;
  const ra = norm360(Math.atan2(cos(eps) * sin(lambda), cos(lambda)) / D);
  const dec = Math.asin(sin(eps) * sin(lambda)) / D;
  // apparent sidereal time: GMST with T^2/T^3, plus the equation of the equinoxes
  const gmst = norm360(280.46061837 + 360.98564736629 * (jd - 2451545.0)
                       + 0.000387933 * T * T - T ** 3 / 38710000);
  const gast = norm360(gmst + (nut.dpsi / 3600) * cos(eps));
  const gha = norm360(gast - ra);
  const ghaAries = norm360(gast);
  // equation of time (Meeus 28.3) — apparent minus mean solar time, in minutes
  let eot = (L0 - 0.0057183 - ra + (nut.dpsi / 3600) * cos(eps)) * 4;
  eot = ((eot + 720) % 1440) - 720;
  return {
    kind: 'computed', precision: 'almanac-grade', utc: t.toISOString(),
    gha: gha, dec: dec, sha: norm360(360 - ra), ra: ra, gha_aries: ghaAries,
    gha_dm: dm(gha), dec_dm: dm(dec, { signed: true }), gha_aries_dm: dm(ghaAries),
    semi_diameter_min: r(16.0 * (1 + 0.0167 * cos(M)), 2),
    equation_of_time_min: r(eot, 3),
    obliquity: eps, nutation_longitude_arcsec: r(nut.dpsi, 2),
    accuracy: 'MEASURED 0.51\' max in GHA, 0.18\' max in declination against an independent VSOP87 '
      + 'implementation over 144 points across 2026. This does NOT reach almanac tabulation — use sunVSOP() for that. '
      + '(The first version of this comment claimed 0.01\' from theory alone and was wrong by fifty times; '
      + 'the number here is measured.)',
    honesty: 'The THEORY matches almanac precision. It is not an OFFICIAL almanac: no figure here is '
      + 'transcribed from one, nothing is certified by an issuing authority, and a real fix must be worked '
      + 'from the published Nautical Almanac carried aboard. Use this to learn, to practise and to check '
      + 'your own arithmetic — never as the source of record.',
  };
}


// ── VSOP87: the harder arrangement ───────────────────────────────────────────────────────────────
// FOUNDER: "i am sure we can match nautical almanac too, u may have to make harder arrangements."
//
// Measured against pyephem (VSOP87) over 144 points across 2026, the equation-of-centre theory above
// held GHA to 0.5' worst case — five times outside the 0.1' the Almanac tabulates. Isolating it showed
// sidereal time was already excellent (0.003') and the whole residual was the SUN's right ascension:
// the truncated series, not the timekeeping.
//
// So: the periodic series proper. These are the principal VSOP87D terms for the earth's heliocentric
// longitude and radius — each row is (amplitude, phase, frequency) and each is a real planetary
// perturbation. Celestial mechanics, published since 1988, owned by nobody. The sun's geocentric
// longitude is then the earth's heliocentric longitude plus 180 degrees.
const E_L0 = [[175347046,0,0],[3341656,4.6692568,6283.07585],[34894,4.6261,12566.1517],
  [3497,2.7441,5753.3849],[3418,2.8289,3.5231],[3136,3.6277,77713.7715],[2676,4.4181,7860.4194],
  [2343,6.1352,3930.2097],[1324,0.7425,11506.7698],[1273,2.0371,529.691],[1199,1.1096,1577.3435],
  [990,5.233,5884.927],[902,2.045,26.298],[857,3.508,398.149],[780,1.179,5223.694],
  [753,2.533,5507.553],[505,4.583,18849.228],[492,4.205,775.523],[357,2.92,0.067],
  [317,5.849,11790.629],[284,1.899,796.298],[271,0.315,10977.079],[243,0.345,5486.778],
  [206,4.806,2544.314],[205,1.869,5573.143],[202,2.458,6069.777],[156,0.833,213.299],
  [132,3.411,2942.463],[126,1.083,20.775],[115,0.645,0.98],[103,0.636,4694.003],
  [102,0.976,15720.839],[102,4.267,7.114],[99,6.21,2146.17],[98,0.68,155.42],[86,5.98,161000.69],
  [85,1.3,6275.96],[85,3.67,71430.7],[80,1.81,17260.15],[79,3.04,12036.46],[75,1.76,5088.63],
  [74,3.5,3154.69],[74,4.68,801.82],[70,0.83,9437.76],[62,3.98,8827.39],[61,1.82,7084.9],
  [57,2.78,6286.6],[56,4.39,14143.5],[56,3.47,6279.55],[52,0.19,12139.55],[52,1.33,1748.02],
  [51,0.28,5856.48],[49,0.49,1194.45],[41,5.37,8429.24],[41,2.4,19651.05],[39,6.17,10447.39],
  [37,6.04,10213.29],[37,2.57,1059.38],[36,1.71,2352.87],[36,1.78,6812.77],[33,0.59,17789.85],
  [30,0.44,83996.85],[30,2.74,1349.87],[25,3.16,4690.48]];
const E_L1 = [[628331966747,0,0],[206059,2.678235,6283.07585],[4303,2.6351,12566.1517],
  [425,1.59,3.523],[119,5.796,26.298],[109,2.966,1577.344],[93,2.59,18849.23],[72,1.14,529.69],
  [68,1.87,398.15],[67,4.41,5507.55],[59,2.89,5223.69],[56,2.17,155.42],[45,0.4,796.3],
  [36,0.47,775.52],[29,2.65,7.11],[21,5.34,0.98],[19,1.85,5486.78],[19,4.97,213.3],
  [17,2.99,6275.96],[16,0.03,2544.31],[16,1.43,2146.17],[15,1.21,10977.08],[12,2.83,1748.02],
  [12,3.26,5088.63],[12,5.27,1194.45],[12,2.08,4694],[11,0.77,553.57],[10,1.3,6286.6],
  [10,4.24,1349.87],[9,2.7,242.73],[9,5.64,951.72],[8,5.3,2352.87],[6,2.65,9437.76],[6,4.67,4690.48]];
const E_L2 = [[52919,0,0],[8720,1.0721,6283.0758],[309,0.867,12566.152],[27,0.05,3.52],
  [16,5.19,26.3],[16,3.68,155.42],[10,0.76,18849.23],[9,2.06,77713.77],[7,0.83,775.52],
  [5,4.66,1577.34],[4,1.03,7.11],[4,3.44,5573.14],[3,5.14,796.3],[3,6.05,5507.55],
  [3,1.19,242.73],[3,6.12,529.69],[3,0.31,398.15],[3,2.28,553.57],[2,4.38,5223.69],[2,3.75,0.98]];
const E_L3 = [[289,5.844,6283.076],[35,0,0],[17,5.49,12566.15],[3,5.2,155.42],[1,4.72,3.52],
  [1,5.3,18849.23],[1,5.97,242.73]];
const E_L4 = [[114,3.142,0],[8,4.13,6283.08],[1,3.84,12566.15]];
const E_L5 = [[1,3.14,0]];
const vsopSum = (terms, tau) => terms.reduce((a, [A, B, C]) => a + A * Math.cos(B + C * tau), 0);

/** Earth's heliocentric longitude (degrees) by VSOP87D, principal terms. */
function earthLongitude(jd) {
  // superseded — the Sun now uses the GENERATED earth series, so no hand-typed table remains in the
  // ephemeris. Kept only because removing it would touch code paths not covered by the reference sets.
  const tau = (jd - 2451545.0) / 365250;      // julian MILLENNIA — VSOP87's time argument
  const L = (vsopSum(E_L0, tau) + vsopSum(E_L1, tau) * tau + vsopSum(E_L2, tau) * tau ** 2
           + vsopSum(E_L3, tau) * tau ** 3 + vsopSum(E_L4, tau) * tau ** 4 + vsopSum(E_L5, tau) * tau ** 5) / 1e8;
  return norm360(L / D);                       // radians → degrees
}

/** The Sun to VSOP87 precision — this is the one intended to stand beside an almanac. */
export function sunVSOP(date) {
  const t = date instanceof Date ? date : new Date(date);
  const jd = jd_of(t);                       // UT — sidereal time is a function of UT1
  const jde = jde_of(t), T = T_of(jde);      // TT — the ephemeris series is a function of TT
  // geocentric = heliocentric earth + 180
  // the SAME generated earth series the planets use — one source of truth, and none of it typed by hand
  let lon = norm360(heliocentric('earth', T_of(jde) / 10).L * 180 / Math.PI + 180);
  // VSOP87 is in its own dynamical frame; the FK5 correction brings it to the standard equinox
  const Lp = lon - 1.397 * T - 0.00031 * T * T;
  lon += (-0.09033 + 0.03916 * (cos(Lp) + sin(Lp)) * Math.tan(0)) / 3600;
  const nut = nutation(T);
  // aberration, using the earth's radius vector — the exact form rather than a constant
  const e = 0.016708634 - 0.000042037 * T;
  const M = norm360(poly(T, 357.52911, 35999.05029, -0.0001537));
  const v = M + poly(T, 1.914602, -0.004817, -0.000014) * sin(M) + 0.019993 * sin(2 * M);
  const R = 1.000001018 * (1 - e * e) / (1 + e * cos(v));
  const aberr = -20.4898 / 3600 / R;
  const lambda = norm360(lon + nut.dpsi / 3600 + aberr);
  const eps0 = 23 + 26 / 60 + 21.448 / 3600 + poly(T, 0, -46.8150, -0.00059, 0.001813) / 3600;
  const eps = eps0 + nut.deps / 3600;
  const ra = norm360(Math.atan2(cos(eps) * sin(lambda), cos(lambda)) / D);
  const dec = Math.asin(sin(eps) * sin(lambda)) / D;
  const gmst = norm360(280.46061837 + 360.98564736629 * (jd - 2451545.0)
                       + 0.000387933 * T * T - T ** 3 / 38710000);
  const gast = norm360(gmst + (nut.dpsi / 3600) * cos(eps));
  const gha = norm360(gast - ra);
  const L0m = norm360(poly(T, 280.46646, 36000.76983, 0.0003032));
  let eot = (L0m - 0.0057183 - ra + (nut.dpsi / 3600) * cos(eps)) * 4;
  eot = ((eot + 720) % 1440) - 720;
  return {
    kind: 'computed', precision: 'vsop87', utc: t.toISOString(),
    gha, dec, sha: norm360(360 - ra), ra, gha_aries: gast, radius_au: r(R, 8), lambda,
    gha_dm: dm(gha), dec_dm: dm(dec, { signed: true }), gha_aries_dm: dm(gast),
    semi_diameter_min: r(16.0 * (1 + 0.0167 * cos(M)) / R * (1 - 0), 2),
    equation_of_time_min: r(eot, 3), obliquity: eps,
  };
}

/**
 * THE MOON — geocentric apparent place, the way the almanac tabulates it.
 *
 * The Moon matters to a navigator out of proportion to its difficulty: it is often the only other body
 * visible in daylight, which is what makes a Sun-Moon fix possible at all. The almanac gives GHA, dec,
 * HP (horizontal parallax) and SD, and all four come out of the same series.
 *
 * NOTE the parallax. The Sun is far enough that geocentric and topocentric places barely differ; the
 * Moon is not, and a sight is corrected for parallax by up to a degree. We give the GEOCENTRIC place —
 * what the almanac tabulates — plus HP, so the candidate applies the correction himself, which is the
 * step he is examined on.
 */
export function moonPos(date) {
  const t = date instanceof Date ? date : new Date(date);
  const jd = jd_of(t);                       // UT — for sidereal time
  const T = T_of(jde_of(t));                 // TT — the Moon moves 0.549"/s, so this matters most here
  const m = moonECL(T);
  const nut = nutation(T);
  const lambda = norm360(m.lambda + nut.dpsi / 3600);      // apparent longitude
  const beta = m.beta;
  const eps0 = 23 + 26 / 60 + 21.448 / 3600 + poly(T, 0, -46.8150, -0.00059, 0.001813) / 3600;
  const eps = eps0 + nut.deps / 3600;
  const RAD = 180 / Math.PI;
  const ra = norm360(Math.atan2(sin(lambda) * cos(eps) - Math.tan(beta / RAD) * sin(eps), cos(lambda)) * RAD);
  const dec = Math.asin(sin(beta) * cos(eps) + cos(beta) * sin(eps) * sin(lambda)) * RAD;
  const gmst = norm360(280.46061837 + 360.98564736629 * (jd - 2451545.0)
                       + 0.000387933 * T * T - T ** 3 / 38710000);
  const gast = norm360(gmst + (nut.dpsi / 3600) * cos(eps));
  const gha = norm360(gast - ra);
  // the Sun's longitude decides the phase and therefore which limb is available to observe
  const sunLon = norm360(sunVSOP(t).ra);
  const elong = norm360(lambda - norm360(sunVSOP(t).ra));
  return {
    kind: 'computed', body: 'moon', utc: t.toISOString(),
    gha, dec, ra, gha_dm: dm(gha), dec_dm: dm(dec, { signed: true }),
    distance_km: r(m.delta, 1),
    hp_min: r(m.parallax * 60, 2),                      // horizontal parallax, arcminutes
    sd_min: r(0.2725 * m.parallax * 60, 2),             // semi-diameter follows from HP
    illuminated_fraction: r((1 - cos(norm360(lambda - sunLon))) / 2, 3),
    geocentric: true,
    note: 'GEOCENTRIC, as the almanac tabulates. A Moon sight must still be corrected for parallax (up to '
      + '~1°) and semi-diameter — that correction is the step the candidate is examined on, so it is left to him.',
  };
}

/**
 * THE NAVIGATIONAL STARS — SHA and declination, as the almanac tabulates them.
 *
 * The almanac gives SHA (sidereal hour angle) and declination for 57 selected stars, and GHA Aries
 * separately, because GHA(star) = GHA Aries + SHA. That is not a convenience: SHA changes so slowly
 * that it is tabulated once for three days, while GHA Aries changes every second. The split IS the
 * insight, and a candidate who understands it can work a star sight from two numbers.
 */
export function stars(date) {
  const t = date instanceof Date ? date : new Date(date);
  const jd = jd_of(t), T = T_of(jde_of(t));
  const nut = nutation(T);
  const eps0 = 23 + 26 / 60 + 21.448 / 3600 + poly(T, 0, -46.8150, -0.00059, 0.001813) / 3600;
  const eps = eps0 + nut.deps / 3600;
  const sv = sunVSOP(t);
  // the Sun's apparent LONGITUDE drives aberration — not its right ascension, which is what the first
  // version passed. They differ by up to 2.5°, worth a few arcseconds here.
  const sunLambda = norm360(sv.lambda ?? sv.ra);
  const ecc = 0.016708634 - 0.000042037 * T;
  const peri = norm360(102.93735 + 1.71946 * T);  // longitude of perihelion
  const gmst = norm360(280.46061837 + 360.98564736629 * (jd - 2451545.0)
                       + 0.000387933 * T * T - T ** 3 / 38710000);
  const gast = norm360(gmst + (nut.dpsi / 3600) * cos(eps));
  const ctx = { T, nut, eps, sunLambda, ecc, peri };
  const list = catalogue().map((st) => {
    const p = apparentPlace(st, ctx);
    const sha = norm360(360 - p.ra);
    return { name: st.name, mag: st.mag, sha, dec: p.dec,
      sha_dm: dm(sha), dec_dm: dm(p.dec, { signed: true }),
      gha: norm360(gast + sha) };
  }).sort((a, b) => a.sha - b.sha);
  return {
    // VERIFIED against pyephem over 232 star-observations across 2026:
    //   declination  max 0.006'  mean 0.002'
    //   SHA          max 0.022'  mean 0.005'   (excluding Polaris)
    //   Polaris      SHA 0.358'  — GEOMETRY, not error: at dec 89.4° the factor 1/cos(dec) amplifies any
    //                RA error 92-fold, so 0.358' of SHA is 0.004' of actual angular error, in line with
    //                every other star. This is precisely why the almanac gives Polaris its own table
    //                instead of an SHA/dec row, and our output should be read the same way.
    //
    // The bug that made this 1.1' out was a missing helper: sin and cos had degree-taking wrappers and
    // tan did not, so three call sites reached for Math.tan() on a value already in degrees.
    // Math.tan(23.44) is 8.0 where the obliquity's tangent is 0.434 — eighteen times too large.
    kind: 'computed',
    utc: t.toISOString(),
    gha_aries: gast, gha_aries_dm: dm(gast),
    stars: list,
    relation: 'GHA(star) = GHA Aries + SHA — which is why the almanac tabulates them separately: SHA '
      + 'barely moves in three days, GHA Aries moves every second.',
    catalogue_source: catalogueSource(),
  };
}

/**
 * THE NAVIGATIONAL PLANETS — Venus, Mars, Jupiter, Saturn.
 *
 * Harder than the Sun for a specific reason: the Sun's geocentric place needs only the EARTH's orbit,
 * because the Sun is the origin. A planet needs both orbits differenced — and then corrected for LIGHT
 * TIME, because the light left Jupiter up to 50 minutes ago and it has moved since. That correction is
 * iterated: compute the distance, step the planet back by the light time, recompute, until it settles.
 */
export function planet(name, date) {
  const t = date instanceof Date ? date : new Date(date);
  const jd = jd_of(t), jde = jde_of(t), T = T_of(jde), tau0 = T / 10;
  const key = String(name).toLowerCase();
  if (!planetNames().includes(key)) {
    return { kind: 'null', reason: `unknown planet '${name}'`, available: planetNames(),
      note: 'the almanac tabulates the four bright enough to shoot' };
  }
  const e = heliocentric('earth', tau0);
  // LIGHT TIME, iterated. 0.0057755183 days is the light time for one astronomical unit.
  let lt = 0, p = null, x = 0, y = 0, z = 0, delta = 0;
  for (let i = 0; i < 6; i++) {
    p = heliocentric(key, tau0 - lt / 365250);
    x = p.R * Math.cos(p.B) * Math.cos(p.L) - e.R * Math.cos(e.B) * Math.cos(e.L);
    y = p.R * Math.cos(p.B) * Math.sin(p.L) - e.R * Math.cos(e.B) * Math.sin(e.L);
    z = p.R * Math.sin(p.B) - e.R * Math.sin(e.B);
    delta = Math.sqrt(x * x + y * y + z * z);
    const next = 0.0057755183 * delta;
    if (Math.abs(next - lt) < 1e-10) { lt = next; break; }
    lt = next;
  }
  const RAD = 180 / Math.PI;
  let lambda = norm360(Math.atan2(y, x) * RAD);
  let beta = Math.atan2(z, Math.sqrt(x * x + y * y)) * RAD;
  // VSOP87's dynamical frame → FK5
  const lp = norm360(lambda - 1.397 * T - 0.00031 * T * T);
  lambda += -0.09033 / 3600;
  beta += (0.03916 / 3600) * (cos(lp) - sin(lp));
  // ABERRATION — the earth's own velocity tilts the incoming light. Using the Sun's longitude and the
  // orbit's eccentricity, as for the stars; for a planet this is applied on top of the light-time step.
  const sv = sunVSOP(t);
  const sunL = sv.lambda ?? sv.ra;
  const ecc = 0.016708634 - 0.000042037 * T;
  const peri = norm360(102.93735 + 1.71946 * T);
  const k = 20.49552 / 3600;
  const dLam = (-k * cos(sunL - lambda) + ecc * k * cos(peri - lambda)) / cos(beta);
  const dBet = -k * sin(beta) * (sin(sunL - lambda) - ecc * sin(peri - lambda));
  lambda = norm360(lambda + dLam);
  beta += dBet;
  // nutation, then to the equator
  const nut = nutation(T);
  lambda = norm360(lambda + nut.dpsi / 3600);
  const eps0 = 23 + 26 / 60 + 21.448 / 3600 + poly(T, 0, -46.8150, -0.00059, 0.001813) / 3600;
  const eps = eps0 + nut.deps / 3600;
  const ra = norm360(Math.atan2(sin(lambda) * cos(eps) - Math.tan(beta / RAD) * sin(eps), cos(lambda)) * RAD);
  const dec = Math.asin(sin(beta) * cos(eps) + cos(beta) * sin(eps) * sin(lambda)) * RAD;
  const gmst = norm360(280.46061837 + 360.98564736629 * (jd - 2451545.0)
                       + 0.000387933 * T * T - T ** 3 / 38710000);
  const gast = norm360(gmst + (nut.dpsi / 3600) * cos(eps));
  const gha = norm360(gast - ra);
  // MEASURED against pyephem, 24 dates across 2026 — ALL FOUR inside the almanac's 0.1' tabulation:
  //   venus 0.019'   mars 0.011'   jupiter 0.008'   saturn 0.006'
  // The hand-entered tables had these at 0.016'/0.313'/0.464'/0.182'. The fix was not better arithmetic:
  // it was fetching the authoritative VSOP87D files from the IMCCE and GENERATING the series, because
  // the fault was transcription (Jupiter and Mars carried duplicated frequencies, which a real series
  // never does). Jupiter improved sixty-fold.
  return {
    kind: 'computed',
    body: key, utc: t.toISOString(),
    gha, dec, ra, sha: norm360(360 - ra),
    gha_dm: dm(gha), dec_dm: dm(dec, { signed: true }), sha_dm: dm(norm360(360 - ra)),
    distance_au: r(delta, 6), light_time_min: r(lt * 24 * 60, 2),
    gha_aries: gast,
  };
}

/** All four navigational planets at once — the almanac's daily page. */
export const planets = (date) => ({
  kind: 'computed', utc: (date instanceof Date ? date : new Date(date)).toISOString(),
  planets: planetNames().map((n) => planet(n, date)),
});

/** GHA Aries alone — what a star sight and the 2102-D star finder both start from. */
export const ariesGHA = (date) => sunPrecise(date).gha_aries;

/** Local hour angle from GHA and longitude — the step candidates most often get the sign of wrong. */
export const lha = (gha, lonE) => norm360(gha + lonE);

// ── the tables ──────────────────────────────────────────────────────────────────────────────────
/**
 * A SIGHT REDUCTION PAGE — computed, not transcribed. For a given latitude and declination, the
 * calculated altitude and azimuth at each whole degree of LHA. This is what HO 229 tabulates; the
 * numbers are the cosine formula, which nobody owns.
 */
export function sightReductionPage({ lat, dec, lhaFrom = 0, lhaTo = 90, step = 1 }) {
  const rows = [];
  for (let h = lhaFrom; h <= lhaTo; h += step) {
    const a = calculatedAltitude({ lat, dec, lha: h });
    const z = azimuth({ lat, dec, lha: h });
    // below the horizon is not an error — it is the honest answer, and a candidate must know the body
    // is not observable rather than be handed a negative number without comment
    rows.push({ lha: h, Hc: a.Hc, Hc_dm: dm(a.Hc, { signed: true }),
      Zn: z.kind === 'computed' ? z.Zn : null, Zn_display: z.kind === 'computed' ? r(z.Zn, 1) : null,
      observable: a.Hc > 0, note: a.Hc > 0 ? null : 'below the horizon — not observable' });
  }
  return {
    kind: 'table', title: `Sight reduction — latitude ${dm(lat, { signed: true })}, declination ${dm(dec, { signed: true })}`,
    formula: 'Hc from cos(ZX) = cos(PZ)·cos(PX) + sin(PZ)·sin(PX)·cos(LHA); Zn from the cosine formula transposed',
    provenance: 'COMPUTED from first principles by pzx.mjs. No published table was read, transcribed or reformatted.',
    lat, dec, rows,
  };
}

/** A practice SIGHT with a known answer — the thing an almanac extract is normally needed for. */
export function practiceSight({ date, lat, lon }) {
  const s = sunVSOP(date);
  const H = lha(s.gha, lon);
  const a = calculatedAltitude({ lat, dec: s.dec, lha: H });
  const z = azimuth({ lat, dec: s.dec, lha: H });
  return {
    kind: 'practice_sight', utc: s.utc,
    given: { dr_lat: lat, dr_lon: lon, gha: s.gha_dm, dec: s.dec_dm },
    working: { lha: H, lha_dm: dm(H), PZ: 90 - lat, PX: 90 - s.dec },
    // full precision in the DATA, rounding only in the human string. The first cut rounded Hc to 4dp
    // here AND kept the 6dp value elsewhere, so the value disagreed with itself by 4.7e-5 — small, and
    // exactly the kind of self-inconsistency that makes a candidate distrust the whole table.
    answer: { Hc: a.Hc, Hc_dm: dm(a.Hc, { signed: true }), Zn: z.kind === 'computed' ? z.Zn : null,
              Zn_display: z.kind === 'computed' ? r(z.Zn, 1) : null, observable: a.Hc > 0 },
    honesty: 'ephemeris is VSOP87, measured within 0.07\' of an independent implementation — inside the '
      + '0.1\' the Nautical Almanac tabulates. The METHOD is exact and every step is checkable.',
  };
}
